import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Button,
  RadioGroup,
  FormField,
  Alert,
  ProgressBar,
  Flashbar,
  Modal,
  TextContent,
  Icon,
  Badge,
} from '@cloudscape-design/components';
import { useDemoContext } from '../context/DemoContext';
import apiService from '../services/api';

function ManualAnalysisPage() {
  const navigate = useNavigate();
  const { selectedFile, manualAnswers, resetDemo, setManualAnalysisResults } = useDemoContext();
  
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes in seconds
  const [answers, setAnswers] = useState(manualAnswers || {});
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [timeTaken, setTimeTaken] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get motivational message based on persona
  const getMotivationalMessage = () => {
    if (!selectedFile || !selectedFile.persona) return "";
    
    const persona = selectedFile.persona;
    const name = persona.name.split(" ")[0]; // Get first name
    
    switch (selectedFile.id) {
      case 'file1':
        return `Now let's get to work, ${name}! Find those investment details for your clients.`;
      case 'file2':
        return `Time to dive in, ${name}! Those compliance issues won't find themselves.`;
      case 'file3':
        return `Ready to analyze, ${name}? Let's discover those key investment insights!`;
      default:
        return `Let's get started, ${name}!`;
    }
  };
  
  // Redirect if no file is selected
  useEffect(() => {
    if (!selectedFile) {
      navigate('/upload');
    }
  }, [selectedFile, navigate]);

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const fileId = selectedFile ? selectedFile.id : 'file1';
        console.log(`Fetching questions for file: ${fileId}`);
        const questionsData = await apiService.getQuestions(fileId);
        console.log('Questions data:', questionsData);
        setQuestions(questionsData);
        setError(null);
      } catch (err) {
        setError('Failed to load questions. Please try again later.');
        console.error('Error loading questions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedFile) {
      fetchQuestions();
    }
  }, [selectedFile]);
  
  // Set start time when component mounts
  useEffect(() => {
    if (!startTime) {
      setStartTime(Date.now());
    }
  }, [startTime]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Calculate time taken when time is up
      const elapsedTime = (Date.now() - startTime) / 1000;
      setTimeTaken(elapsedTime);
      setShowTimeUpModal(true);
    }
  }, [timeRemaining, startTime]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleReset = () => {
    resetDemo();
    navigate('/');
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const handleSubmit = () => {
    // Calculate time taken when user submits
    const elapsedTime = (Date.now() - startTime) / 1000;
    setTimeTaken(elapsedTime);
    
    console.log("Submitting answers:", answers);
    console.log("Selected file:", selectedFile);
    
    // Save results to context
    setManualAnalysisResults(selectedFile, answers, elapsedTime);
    
    // Navigate to the automated analysis page
    navigate('/analysis');
  };

  const handleTimeUp = () => {
    setShowTimeUpModal(false);
    handleSubmit();
  };

  // Determine timer color based on time remaining
  const getTimerColor = () => {
    if (timeRemaining > 60) return "information.light"; // More than 1 minute: blue
    if (timeRemaining > 30) return "warning.light";     // 30-60 seconds: yellow
    return "error.light";                               // Less than 30 seconds: red
  };

  const getTimerTextColor = () => {
    if (timeRemaining > 60) return "information.dark"; // More than 1 minute: blue
    if (timeRemaining > 30) return "warning.dark";     // 30-60 seconds: yellow
    return "error.dark";                               // Less than 30 seconds: red
  };

  // Persona component - more compact version
  const PersonaHeader = () => {
    if (!selectedFile || !selectedFile.persona) return null;
    
    const persona = selectedFile.persona;
    
    return (
      <Box padding="xs" backgroundColor="background.container">
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <img 
            src={persona.avatar} 
            alt={`${persona.name}'s avatar`} 
            style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid #eaeded'
            }} 
          />
          <div style={{ flex: 1 }}>
            <TextContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontWeight: 'bold', marginRight: '8px' }}>{persona.name}</span>
                  <Badge>{persona.jobTitle}</Badge>
                </div>
                <Box color="text-status-info" fontSize="body-m" fontWeight="bold">
                  {getMotivationalMessage()}
                </Box>
              </div>
              <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#545b64', margin: '4px 0 0 0' }}>
                <strong>Current Process:</strong> {persona.currentProcess.split('.')[0]}.
              </div>
            </TextContent>
          </div>
        </SpaceBetween>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Loading Questions</Header>
          <ProgressBar label="Loading" indeterminate={true} />
        </SpaceBetween>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Error</Header>
          <Alert type="error">{error}</Alert>
          <Button onClick={() => navigate('/upload')}>Back to Document Selection</Button>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          description="Find answers to the following questions in the document within 2 minutes."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Box
                padding="xs"
                backgroundColor={getTimerColor()}
                borderRadius="standard"
                display="inline-block"
              >
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <Icon name="time" size="medium" />
                  <Box variant="h3" color={getTimerTextColor()} fontWeight="bold">
                    {formatTime(timeRemaining)}
                  </Box>
                </SpaceBetween>
              </Box>
              <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
              <Button variant="primary" onClick={handleSubmit}>Submit Answers</Button>
            </SpaceBetween>
          }
        >
          Manual Document Analysis
        </Header>

        <PersonaHeader />

        <Flashbar
          items={[
            {
              type: "in-progress",
              content: (
                <ProgressBar
                  label="Time elapsed"
                  description="Review the document and answer the questions below. After 2 minutes, you'll be automatically directed to the AI-powered analysis."
                  value={(120 - timeRemaining) / 120 * 100}
                  additionalInfo=""
                  variant="flash"
                />
              ),
              dismissible: false,
              dismissLabel: "Dismiss message",
              onDismiss: () => setItems([]),
              id: "message_1"
            }
          ]}
        />

        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box padding="s">
              <Header variant="h2">Document Preview</Header>
              <div style={{ height: '650px', border: '1px solid #eaeded', overflow: 'auto' }}>
                <iframe
                  src={selectedFile ? selectedFile.path : '/AusSuper-PDS.pdf'}
                  title="PDF Document"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </div>
            </Box>
          </div>
          <div>
            <Box padding="s">
              <Header variant="h2">Questions</Header>
              <SpaceBetween size="l">
                {questions.map((q) => (
                  <FormField
                    key={q.id}
                    label={q.question}
                    description={`Response type: ${q.responseType}`}
                  >
                    <RadioGroup
                      items={q.options}
                      value={answers[q.id] || ''}
                      onChange={({ detail }) => handleAnswerChange(q.id, detail.value)}
                    />
                  </FormField>
                ))}
              </SpaceBetween>
            </Box>
          </div>
        </ColumnLayout>
      </SpaceBetween>

      <Modal
        visible={showTimeUpModal}
        header="Time's Up!"
        closeAriaLabel="Close modal"
        onDismiss={handleTimeUp}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
              <Button variant="primary" iconName="gen-ai" onClick={handleTimeUp}>
                Proceed to AI Analysis
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          {selectedFile && selectedFile.persona ? (
            <>
              <Box padding="s" textAlign="center">
                <img 
                  src={selectedFile.persona.avatar} 
                  alt={`${selectedFile.persona.name}'s avatar`} 
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #eaeded',
                    margin: '0 auto 10px auto'
                  }} 
                />
                <h3 style={{ margin: '5px 0' }}>{selectedFile.persona.name}</h3>
                <Badge>{selectedFile.persona.jobTitle}</Badge>
              </Box>
              <p>
                <strong>Time's up!</strong> This is a typical challenge {selectedFile.persona.name.split(" ")[0]} faces every day - 
                limited time to extract critical information from lengthy documents.
              </p>
              <p>
                Let's see how AI can help {selectedFile.persona.name.split(" ")[0]} analyze this document more efficiently.
              </p>
            </>
          ) : (
            <>
              <p>
                Your 2 minutes for manual analysis are up! Let's see how AI can help analyze this document.
              </p>
              <p>
                Click "Proceed to AI Analysis" to continue or "Reset Demo" to start over.
              </p>
            </>
          )}
        </TextContent>
      </Modal>
    </Container>
  );
}

export default ManualAnalysisPage;
